/**
 * Lucide 스타일 SVG 아이콘 모음.
 *
 * 외부 의존성 없이 번들 크기 최소화를 위해 자주 쓰는 것만 직접 작성.
 * 모든 아이콘은 24x24 viewBox, 1.8 stroke-width, 라운드 캡.
 */

type IconProps = { size?: number; className?: string; strokeWidth?: number }

function base({ size = 18, className = '', strokeWidth = 1.8 }: IconProps, body: React.ReactNode) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      {body}
    </svg>
  )
}

export const FolderIcon = (p: IconProps) =>
  base(p, <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7z" />)

export const LayersIcon = (p: IconProps) =>
  base(p, (
    <>
      <path d="M12 3 2 8l10 5 10-5-10-5z" />
      <path d="M2 17l10 5 10-5" />
      <path d="M2 12l10 5 10-5" />
    </>
  ))

export const CameraIcon = (p: IconProps) =>
  base(p, (
    <>
      <path d="M14.5 4h-5L8 6H4a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-4l-1.5-2z" />
      <circle cx="12" cy="13" r="3.5" />
    </>
  ))

export const ClipboardIcon = (p: IconProps) =>
  base(p, (
    <>
      <path d="M9 4h6a1 1 0 0 1 1 1v1H8V5a1 1 0 0 1 1-1z" />
      <path d="M8 6H6a2 2 0 0 0-2 2v11a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-2" />
    </>
  ))

export const ShieldIcon = (p: IconProps) =>
  base(p, <path d="M12 3 4 6v6c0 5 3.5 8.5 8 9 4.5-.5 8-4 8-9V6l-8-3z" />)

export const SparklesIcon = (p: IconProps) =>
  base(p, (
    <>
      <path d="M12 3v3M12 18v3M3 12h3M18 12h3M5.5 5.5l2 2M16.5 16.5l2 2M5.5 18.5l2-2M16.5 7.5l2-2" />
      <circle cx="12" cy="12" r="3.5" />
    </>
  ))

export const ContractIcon = (p: IconProps) =>
  base(p, (
    <>
      <path d="M6 3h9l5 5v13a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z" />
      <path d="M14 3v6h6" />
      <path d="M9 13h6M9 17h4" />
    </>
  ))

export const HomeIcon = (p: IconProps) =>
  base(p, (
    <>
      <path d="m3 11 9-7 9 7" />
      <path d="M5 10v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V10" />
      <path d="M10 21v-6h4v6" />
    </>
  ))

export const UploadIcon = (p: IconProps) =>
  base(p, (
    <>
      <path d="M12 3v13" />
      <path d="m7 8 5-5 5 5" />
      <path d="M5 21h14" />
    </>
  ))

export const CheckIcon = (p: IconProps) => base(p, <path d="m5 12 5 5 9-10" />)

export const XIcon = (p: IconProps) =>
  base(p, (
    <>
      <path d="M6 6l12 12" />
      <path d="M18 6 6 18" />
    </>
  ))

export const MenuIcon = (p: IconProps) =>
  base(p, (
    <>
      <path d="M4 7h16" />
      <path d="M4 12h16" />
      <path d="M4 17h16" />
    </>
  ))

export const ArrowRightIcon = (p: IconProps) =>
  base(p, (
    <>
      <path d="M5 12h14" />
      <path d="m13 6 6 6-6 6" />
    </>
  ))

export const ArrowLeftIcon = (p: IconProps) =>
  base(p, (
    <>
      <path d="M19 12H5" />
      <path d="m11 18-6-6 6-6" />
    </>
  ))

export const LogoutIcon = (p: IconProps) =>
  base(p, (
    <>
      <path d="M15 4h3a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-3" />
      <path d="m10 16-4-4 4-4" />
      <path d="M6 12h10" />
    </>
  ))

export const PlusIcon = (p: IconProps) =>
  base(p, (
    <>
      <path d="M12 5v14" />
      <path d="M5 12h14" />
    </>
  ))

export const BuildingIcon = (p: IconProps) =>
  base(p, (
    <>
      <path d="M4 21h16" />
      <path d="M6 21V5a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v16" />
      <path d="M10 8h4M10 12h4M10 16h4" />
    </>
  ))

export const ZapIcon = (p: IconProps) =>
  base(p, <path d="M13 3 4 14h7l-1 7 9-11h-7l1-7z" />)

export const PhotoIcon = (p: IconProps) =>
  base(p, (
    <>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <circle cx="9" cy="10" r="1.5" />
      <path d="m3 17 5-5 4 4 3-3 6 6" />
    </>
  ))

export const TargetIcon = (p: IconProps) =>
  base(p, (
    <>
      <circle cx="12" cy="12" r="9" />
      <circle cx="12" cy="12" r="5" />
      <circle cx="12" cy="12" r="1.2" />
    </>
  ))

export const LinkIcon = (p: IconProps) =>
  base(p, (
    <>
      <path d="M9 15a4 4 0 0 1 0-6l3-3a4 4 0 0 1 6 6l-1 1" />
      <path d="M15 9a4 4 0 0 1 0 6l-3 3a4 4 0 0 1-6-6l1-1" />
    </>
  ))

export const GridIcon = (p: IconProps) =>
  base(p, (
    <>
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
      <rect x="14" y="14" width="7" height="7" rx="1" />
    </>
  ))

export const RefreshIcon = (p: IconProps) =>
  base(p, (
    <>
      <path d="M21 12a9 9 0 1 1-2.5-6.3" />
      <path d="M21 3v6h-6" />
    </>
  ))

export const UsersIcon = (p: IconProps) =>
  base(p, (
    <>
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </>
  ))
